// ABOUTME: React-Query hooks for import/export of catalog (country×BL) and global configurations.
// ABOUTME: Export downloads a JSON bundle; validate checks schema drift; import upserts via the API.
import {useMutation, useQueryClient} from '@tanstack/react-query'
import fileDownload from 'js-file-download'

import {api} from '../../../../App.store'

export interface ImportItemResult {
  status: 'Created' | 'Updated' | 'Failed' | string
  scope: string
  reason: string | null
}

export interface ImportReport {
  entityKey: string
  created: number
  updated: number
  failed: number
  results: ImportItemResult[]
}

export interface ImportCompatibilityReport {
  entityKey: string
  compatible: boolean
  issues: string[]
}

const CATALOG_KEY = ['catalogConfig'] as const
const GLOBAL_KEY = ['catalogConfig', 'global'] as const

const stamp = () => new Date().toISOString().slice(0, 10)

export const useExportCatalogConfigs = () =>
  useMutation({
    mutationFn: async () => {
      const res = await api.get('/tuc-configs/country-configurations/export', {responseType: 'blob'})
      fileDownload(res.data as Blob, `catalog-configurations-${stamp()}.json`)
    }
  })

export const useValidateCatalogImport = () =>
  useMutation({
    mutationFn: async (bundle: unknown) => {
      const res = await api.post<ImportCompatibilityReport>(
        '/tuc-configs/country-configurations/import/validate',
        bundle
      )
      return res.data
    }
  })

export const useImportCatalogConfigs = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({bundle, force}: {bundle: unknown; force?: boolean}) => {
      const res = await api.post<ImportReport>('/tuc-configs/country-configurations/import', bundle, {
        params: force ? {force: true} : undefined
      })
      return res.data
    },
    onSuccess: () => queryClient.invalidateQueries({queryKey: CATALOG_KEY})
  })
}

export const useExportGlobalConfig = () =>
  useMutation({
    mutationFn: async () => {
      const res = await api.get('/tuc-configs/global-configuration/export', {responseType: 'blob'})
      fileDownload(res.data as Blob, `global-configuration-${stamp()}.json`)
    }
  })

export const useValidateGlobalImport = () =>
  useMutation({
    mutationFn: async (bundle: unknown) => {
      const res = await api.post<ImportCompatibilityReport>(
        '/tuc-configs/global-configuration/import/validate',
        bundle
      )
      return res.data
    }
  })

export const useImportGlobalConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({bundle, force}: {bundle: unknown; force?: boolean}) => {
      const res = await api.post<ImportReport>('/tuc-configs/global-configuration/import', bundle, {
        params: force ? {force: true} : undefined
      })
      return res.data
    },
    onSuccess: () => queryClient.invalidateQueries({queryKey: GLOBAL_KEY})
  })
}
